import { decode } from 'jsonwebtoken';
import { throttle } from 'lodash';
import App from 'next/app';
import React from 'react';
import { ApolloProvider } from '@apollo/client';
import config from '../common/config';
import {
  checkNextGenSupport,
  detectDevice,
} from '../common/helpers/environment/DetectDeviceHelper';
import {
  DOCUMENT_CLICK,
  emitter,
  WINDOW_RESIZE,
  WINDOW_SCROLL,
} from '../common/helpers/ui/EventHelper';
import FeatureFlagsHelper from '../common/helpers/featureFlags/featureFlagsHelper';
import { withApollo } from '../lib/withApollo';
import { wrapper } from '../store';
import {
  updateCurrentPage,
  updateLoginStatus,
  updateUserAgentDetails,
} from '../store/common/action';
import Error from './_error';
import { recordWebVitalsEvent } from '../common/analytics/events/webVitalEvents';
import { sendAnalytics } from '../common/analytics/analytics';
import { getAnalyticsSource } from '../common/analytics/helpers/sourceHelper';

import './../common/styles/style.scss';
import './../common/styles/normalize.scss';
import 'antd/dist/reset.css';

export function reportWebVitals({ id, name, label, value }) {
  if (label === 'web-vital') {
    const analyticsSource = getAnalyticsSource(window.location.pathname);
    sendAnalytics(recordWebVitalsEvent(id, name, value, analyticsSource));
  }
}

class MyApp extends App {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, info) {
    console.log(error);
    console.log(info);
  }

  static getDerivedStateFromError() {
    // Display fallback UI
    return { hasError: true };
  }

  componentDidMount() {
    if (config.isBrowser) {
      // Bind document 'click' event
      document.addEventListener('click', () => {
        emitter.emit(DOCUMENT_CLICK);
      });

      // Bind window 'resize' event
      window.addEventListener(
        'resize',
        throttle(() => {
          emitter.emit(WINDOW_RESIZE);
        }),
        true,
      );

      // Bind window 'scroll' event
      window.addEventListener(
        'scroll',
        throttle(() => {
          emitter.emit(WINDOW_SCROLL);
        }),
        true,
      );

      window.recaptchaOptions = {
        lang: 'th',
      };
    }
  }

  render() {
    const { Component, pageProps, apollo } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return <Error />;
    }

    return (
      <ApolloProvider client={apollo}>
        <Component {...pageProps} />
      </ApolloProvider>
    );
  }
}

MyApp.getInitialProps = wrapper.getInitialPageProps(
  (store) =>
    async ({ Component, ctx }) => {
      const { req, res, pathname } = ctx;
      const decodedQueryParams = {};
      Object.entries(ctx.query).forEach(([key, value]) => {
        decodedQueryParams[key] = decodeURI(value);
      });
      ctx.query = decodedQueryParams;
      if (req) {
        // Handle redirection if requested by querystring
        const { destination } = req.query;
        if (destination === 'login') {
          res.redirect(config.loginUrl);
        }

        // Set server side cookies context for feature flags helper
        FeatureFlagsHelper.setServerSideCookies(req.cookies);

        const loginCookie = req.cookies['tol-user-session'];
        const deviceType = detectDevice(req.headers);

        const isNextGenSupported = checkNextGenSupport(req.headers);
        store.dispatch(
          updateUserAgentDetails({
            isNextGenSupported: isNextGenSupported,
            deviceType: deviceType,
          }),
        );
        if (loginCookie) {
          const decoded = decode(loginCookie);
          if (decoded.display_name && decoded.email) {
            const userDetails = decode(req.cookies['tol-access-token']);
            const userId = userDetails.username;
            const payload = {
              displayName: decoded.display_name,
              email: decoded.email,
              accessToken: req.cookies['tol-access-token'],
              refreshToken: req.cookies['tol-refresh-token'],
              userId,
            };
            store.dispatch(updateLoginStatus(payload));
          }
        }
      }

      const currentPage = pathname.substring(1);
      store.dispatch(updateCurrentPage(currentPage));

      return {
        pageProps: Component.getInitialProps
          ? await Component.getInitialProps(ctx)
          : {},
      };
    },
);

export default withApollo({ ssr: true })(wrapper.withRedux(MyApp));
